import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Loader2, FlaskConical, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { LabMetricCard, UnavailableLabCard } from "@/components/intelligence/LabMetricCard";
import { LabTrendChart } from "@/components/intelligence/LabTrendChart";
import { LabHospitalComparisonTable } from "@/components/intelligence/LabHospitalComparisonTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatCount, formatHours, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceExportButton } from "@/components/intelligence/IntelligenceExportButton";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { buildLabExportRows } from "@/lib/intelligenceExport";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const PRIMARY_METRIC_KEYS = [
	"lab_revenue",
	"lab_tests",
	"pending_lab_tests",
	"completed_lab_tests",
	"cancelled_lab_tests",
	"revenue_per_test",
	"average_lab_tat",
];

export default function LabIntelligencePage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-lab"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-lab", queryParams],
		queryFn: () => intelligenceApi.getLab(queryParams),
		enabled: customReady,
	});

	const primaryMetrics = useMemo(() => {
		if (!data) return [];
		return PRIMARY_METRIC_KEYS.map((key) => data.metrics.find((m) => m.key === key)).filter(
			(m): m is NonNullable<typeof m> => !!m
		);
	}, [data]);

	const basePath = isSuperAdmin ? "/super-admin" : "/hospital-admin";

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={basePath} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<FlaskConical className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">Lab Intelligence</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Test volume, revenue, TAT and pending workload · {data?.period.label ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end gap-2">
					<IntelligenceExportButton
						filename={`lab-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
						buildRows={() => buildLabExportRows(data)}
						label="Quick CSV"
					/>
					<IntelligenceServerExportButton
						report="lab"
						params={queryParams}
						filename={`lab-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load lab intelligence"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					{data.statements.length > 0 && (
						<Card>
							<CardContent className="pt-4">
								<ul className="space-y-1 text-sm text-slate-700">
									{data.statements.map((s) => (
										<li key={s}>• {s}</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
						{primaryMetrics.map((m) => (
							<LabMetricCard
								key={m.key}
								metric={m}
								comparisonLabel={data.comparisonPeriod?.label}
							/>
						))}
						{data.unavailable.map((u) => (
							<UnavailableLabCard key={u.key} metric={u} />
						))}
					</div>

					<div className="grid lg:grid-cols-3 gap-4">
						<div className="lg:col-span-2">
							<LabTrendChart dailyTrend={data.dailyTrend} />
						</div>
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">Revenue split</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">OPD / Day Care</span>
									<span className="font-medium tabular-nums">{formatInr(data.revenueSplit.opd)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">IPD (service date)</span>
									<span className="font-medium tabular-nums">{formatInr(data.revenueSplit.ipd)}</span>
								</div>
							</CardContent>
						</Card>
					</div>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Turnaround time (proxy)</CardTitle>
							<p className="text-xs text-muted-foreground">{data.tat.definition}</p>
						</CardHeader>
						<CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
							<div>
								<p className="text-muted-foreground">Average</p>
								<p className="font-semibold tabular-nums">
									{data.tat.averageHours != null ? formatHours(data.tat.averageHours) : "—"}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Median</p>
								<p className="font-semibold tabular-nums">
									{data.tat.medianHours != null ? formatHours(data.tat.medianHours) : "—"}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">P90</p>
								<p className="font-semibold tabular-nums">
									{data.tat.p90Hours != null ? formatHours(data.tat.p90Hours) : "—"}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Samples</p>
								<p className="font-semibold tabular-nums">{formatCount(data.tat.sampleCount)}</p>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Top tests</CardTitle>
							<p className="text-xs text-muted-foreground">By revenue in period</p>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Test</TableHead>
										<TableHead className="text-right">Volume</TableHead>
										<TableHead className="text-right">Revenue</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.topTests.map((t) => (
										<TableRow key={t.testKey}>
											<TableCell>{t.testName}</TableCell>
											<TableCell className="text-right tabular-nums">{formatCount(t.volume)}</TableCell>
											<TableCell className="text-right tabular-nums">{formatInr(t.revenue)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					{data.hospitalComparison.enabled && (
						<LabHospitalComparisonTable rows={data.hospitalComparison.rows} />
					)}

					<Card className="border-amber-200/60 bg-amber-50/30">
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Data limitations</CardTitle>
						</CardHeader>
						<CardContent>
							<ul className="space-y-1 text-xs text-muted-foreground">
								{data.limitations.map((l) => (
									<li key={l}>• {l}</li>
								))}
							</ul>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
